"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Flag, Pause, Play, Volume2, VolumeX, X } from "lucide-react";
import ReportModal from "@/components/moderation/ReportModal";
import {
  filterPublicStoryGroups,
  type StoryBusinessGroup,
} from "@/lib/stories/businessStories";
import { getBusinessPublicUrl } from "@/lib/ids/publicRefs";

const IMAGE_DURATION_MS = 6000;

function relativeTime(value: string) {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

function storyHref(group: StoryBusinessGroup, story: StoryBusinessGroup["stories"][number], mode: string) {
  if (story.ctaType === "listing" && (story.ctaListingPublicId || story.ctaListingId)) {
    const ref = encodeURIComponent(story.ctaListingPublicId || story.ctaListingId || "");
    return `${mode === "customer" ? "/customer/listings" : "/listings"}/${ref}`;
  }
  return getBusinessPublicUrl({ public_id: group.publicId, id: group.ownerUserId });
}

export default function StoryViewer({
  groups,
  initialGroupIndex = 0,
  initialStoryIndex = 0,
  mode = "public",
  onSeen,
  onGroupComplete,
  loadNextGroup,
  onClose,
}: {
  groups: StoryBusinessGroup[];
  initialGroupIndex?: number;
  initialStoryIndex?: number;
  mode?: "public" | "customer";
  onSeen?: (storyId: string) => void;
  onGroupComplete?: (group: StoryBusinessGroup) => void;
  loadNextGroup?: (currentBusinessId: string) => Promise<StoryBusinessGroup | null>;
  onClose: () => void;
}) {
  const [loadedGroups, setLoadedGroups] = useState(groups);
  const usableGroups = useMemo(() => filterPublicStoryGroups(loadedGroups), [loadedGroups]);
  const [groupIndex, setGroupIndex] = useState(initialGroupIndex);
  const [storyIndex, setStoryIndex] = useState(initialStoryIndex);
  const [reportOpen, setReportOpen] = useState(false);
  const [documentVisible, setDocumentVisible] = useState(true);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState({ storyId: "", percent: 0 });
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const loadingNextGroupRef = useRef(false);
  const returnFocusRef = useRef<HTMLElement | null>(
    typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null
  );
  const group = usableGroups[groupIndex];
  const story = group?.stories?.[storyIndex];
  const businessHref = group
    ? getBusinessPublicUrl({ public_id: group.publicId, id: group.ownerUserId })
    : "/";

  useEffect(() => {
    setLoadedGroups(groups);
  }, [groups]);

  const goPrevious = useCallback(() => {
    if (storyIndex > 0) return setStoryIndex((value) => value - 1);
    if (groupIndex > 0) {
      const previousIndex = groupIndex - 1;
      setGroupIndex(previousIndex);
      setStoryIndex(Math.max(0, usableGroups[previousIndex].stories.length - 1));
    }
  }, [groupIndex, storyIndex, usableGroups]);

  const goNext = useCallback(() => {
    if (group && storyIndex < group.stories.length - 1) {
      return setStoryIndex((value) => value + 1);
    }
    if (loadingNextGroupRef.current) return;
    if (group) onGroupComplete?.(group);
    if (groupIndex < usableGroups.length - 1) {
      setGroupIndex((value) => value + 1);
      setStoryIndex(0);
      return;
    }
    if (group && loadNextGroup) {
      loadingNextGroupRef.current = true;
      void loadNextGroup(group.businessId)
        .then((nextGroup) => {
          if (!nextGroup) {
            onClose();
            return;
          }
          setLoadedGroups((current) => current.some(
            (candidate) => candidate.businessId === nextGroup.businessId
          ) ? current : [...current, nextGroup]);
          setGroupIndex((value) => value + 1);
          setStoryIndex(0);
        })
        .catch(onClose)
        .finally(() => {
          loadingNextGroupRef.current = false;
        });
      return;
    }
    onClose();
  }, [group, groupIndex, loadNextGroup, onClose, onGroupComplete, storyIndex, usableGroups.length]);

  useEffect(() => {
    const returnFocusElement = returnFocusRef.current;
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      returnFocusElement?.focus?.();
    };
  }, []);

  useEffect(() => {
    const handleVisibility = () => setDocumentVisible(!document.hidden);
    handleVisibility();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    if (story?.id) onSeen?.(story.id);
  }, [onSeen, story?.id]);

  useEffect(() => {
    if (!documentVisible || reportOpen || !story || story.mediaType === "video") return undefined;
    const timer = window.setTimeout(goNext, IMAGE_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [documentVisible, goNext, reportOpen, story]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || story?.mediaType !== "video") return undefined;
    video.currentTime = 0;
    return () => {
      video.pause();
      video.currentTime = 0;
    };
  }, [story?.id, story?.mediaType]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || story?.mediaType !== "video") return;
    if (!documentVisible || reportOpen) {
      video.pause();
      return;
    }
    void video.play().catch(() => {});
  }, [documentVisible, reportOpen, story?.id, story?.mediaType]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (reportOpen) return;
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") goPrevious();
      if (event.key === "ArrowRight") goNext();
      if (event.key === "Tab" && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]')
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrevious, onClose, reportOpen]);

  const cta = useMemo(() => {
    if (!story?.ctaType) return null;
    return {
      href: storyHref(group, story, mode),
      label: story.ctaType === "listing" ? "View item" : "Visit shop",
    };
  }, [group, mode, story]);

  if (!group || !story) return null;

  return (
    <>
      <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 p-0 sm:p-4" role="presentation">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={`${group.businessName} Story`}
          className="theme-lock relative flex h-[100dvh] w-full max-w-[460px] flex-col overflow-hidden bg-black text-white shadow-2xl sm:h-[min(820px,calc(100dvh-2rem))] sm:rounded-3xl"
        >
          <div className="absolute inset-x-0 top-0 z-20 bg-gradient-to-b from-black/80 via-black/35 to-transparent px-3 pb-14 pt-3">
            <div className="flex gap-1" aria-label={`Story ${storyIndex + 1} of ${group.stories.length}`}>
              {group.stories.map((item, index) => (
                <span key={item.id} className="h-1 flex-1 overflow-hidden rounded-full bg-white/35">
                  <span
                    key={`${story.id}-${index}`}
                    className={`block h-full bg-white ${index < storyIndex ? "w-full" : index > storyIndex ? "w-0" : story.mediaType === "video" ? "" : "yb-story-progress"}`}
                    style={index === storyIndex && story.mediaType === "video" ? {
                      width: `${videoProgress.storyId === story.id ? videoProgress.percent : 0}%`,
                    } : undefined}
                  />
                </span>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Link
                href={businessHref}
                onClick={onClose}
                aria-label={`Visit ${group.businessName}`}
                className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-violet-100 text-xs font-bold text-violet-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {group.avatarUrl ? <img src={group.avatarUrl} alt="" className="h-full w-full object-cover" /> : group.businessName.slice(0, 2).toUpperCase()}
              </Link>
              <div className="min-w-0 flex-1">
                <Link
                  href={businessHref}
                  onClick={onClose}
                  className="block truncate text-sm font-semibold text-white hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  {group.businessName}
                </Link>
                <p className="text-xs text-white/70">{relativeTime(story.createdAt)}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const video = videoRef.current;
                  if (!video) return;
                  if (video.paused) void video.play().catch(() => {});
                  else video.pause();
                }}
                className={`${story.mediaType === "video" ? "inline-flex" : "hidden"} rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white`}
                aria-label={videoPlaying ? "Pause Story video" : "Play Story video"}
              >
                {videoPlaying ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
              </button>
              <button
                type="button"
                onClick={() => setVideoMuted((value) => !value)}
                className={`${story.mediaType === "video" ? "inline-flex" : "hidden"} rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white`}
                aria-label={videoMuted ? "Unmute Story video" : "Mute Story video"}
              >
                {videoMuted ? <VolumeX className="h-4 w-4" aria-hidden="true" /> : <Volume2 className="h-4 w-4" aria-hidden="true" />}
              </button>
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white"
                aria-label="Report this Story"
              >
                <Flag className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label="Close Story viewer"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {story.mediaType === "video" ? (
            <video
              key={story.id}
              ref={videoRef}
              src={story.mediaUrl}
              poster={story.mediaThumbUrl || undefined}
              aria-label={story.caption || `${group.businessName} video Story`}
              autoPlay
              muted={videoMuted}
              playsInline
              preload="metadata"
              onTimeUpdate={(event) => {
                const video = event.currentTarget;
                setVideoProgress({
                  storyId: story.id,
                  percent: video.duration > 0
                    ? Math.min(100, (video.currentTime / video.duration) * 100)
                    : 0,
                });
              }}
              onPlay={() => setVideoPlaying(true)}
              onPause={() => setVideoPlaying(false)}
              onEnded={goNext}
              onError={goNext}
              className="h-full w-full object-cover object-center"
            />
          ) : (
            <img
              src={story.mediaUrl}
              alt={story.caption || `${group.businessName} Story`}
              onError={goNext}
              className="h-full w-full object-contain"
            />
          )}

          <button type="button" onClick={goPrevious} className="absolute inset-y-24 left-0 z-10 w-1/3" aria-label="Previous Story" />
          <button type="button" onClick={goNext} className="absolute inset-y-24 right-0 z-10 w-1/3" aria-label="Next Story" />

          {(story.caption || cta) ? (
            <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-20 text-center">
              {story.caption ? <p className="mx-auto max-w-sm text-[15px] font-medium leading-6 text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.65)]">{story.caption}</p> : null}
              {cta ? (
                <Link
                  href={cta.href}
                  onClick={onClose}
                  className="mt-4 inline-flex min-h-11 items-center justify-center rounded-full bg-white px-6 text-sm font-semibold text-slate-950 hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
                >
                  {cta.label}
                </Link>
              ) : null}
            </div>
          ) : null}

          <button type="button" onClick={goPrevious} className="absolute left-2 top-1/2 z-30 hidden -translate-y-1/2 rounded-full bg-black/35 p-2 hover:bg-black/55 sm:block" aria-label="Previous Story">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button type="button" onClick={goNext} className="absolute right-2 top-1/2 z-30 hidden -translate-y-1/2 rounded-full bg-black/35 p-2 hover:bg-black/55 sm:block" aria-label="Next Story">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <ReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType="business"
        targetId={group.ownerUserId}
        targetLabel={`${group.businessName} Story`}
        meta={{ content_type: "story", story_id: story.id, business_id: group.businessId }}
        onSubmitted={() => {}}
        zIndexClassName="z-[100]"
      />
    </>
  );
}
